import { z } from 'zod';
import { get, getApiClient, post } from '../http';
import { resource } from '../schemas/common';
import { dappRegistrationRequestSchema, sessionSchema } from '../schemas/domain';

/**
 * Web3 wallet (DApp) auth — the 2026-07-30 replacement for email+password.
 * See apps/user/src/lib/wallet.ts for the MetaMask/injected-provider connect
 * helper that produces the `signature` these inputs need, and
 * apps/api routes/api.php `auth/wallet/*` for the backend side
 * (App\Http\Controllers\Auth\WalletAuthController).
 */

export const walletNoncePurposeSchema = z.enum(['register', 'login']);
export type WalletNoncePurpose = z.infer<typeof walletNoncePurposeSchema>;

export const walletNonceInput = z.object({
  wallet_address: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'Not a valid wallet address'),
  purpose: walletNoncePurposeSchema,
});
export type WalletNonceInput = z.infer<typeof walletNonceInput>;

const walletNonceResponse = z.object({
  wallet_address: z.string(),
  nonce: z.string(),
  message: z.string(),
  expires_at: z.string(),
});
export type WalletNonceResponse = z.infer<typeof walletNonceResponse>;

export const walletRegisterInput = z.object({
  wallet_address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
  nonce: z.string(),
  signature: z.string().regex(/^0x[a-fA-F0-9]{130}$/, 'Not a valid signature'),
  // Optional as of 2026-07-31: the backend now auto-detects the qualifying
  // LDS transfer on-chain (WalletAuthService::registerViaDapp / recheck), so
  // the frontend no longer asks the member to find and paste their tx hash.
  // Still accepted if a caller supplies one (kept for backward compatibility
  // with the manual-hash path the backend still honours). As of 2026-07-31
  // the frontend DOES supply this: sendLdsPayment() in wallet.ts triggers the
  // transfer itself via the connected wallet, so the tx hash is known up
  // front and passed here for the fast known-hash verification path.
  tx_hash: z
    .string()
    .regex(/^0x[a-fA-F0-9]{64}$/, 'That does not look like a valid transaction hash')
    .optional(),
  sponsor_code: z.string().min(1, 'Sponsor ID is required').max(16),
  leg: z.enum(['left', 'right']).optional(),
  // Added 2026-07-31 alongside the registration form's new contact fields
  // (Register.tsx) — same field names the backend's RegistrationService
  // already uses for its email+password flow (name/phone/email).
  name: z.string().min(2, 'Name is required').max(120),
  phone: z
    .string()
    .max(32)
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v)),
  email: z
    .string()
    .email('Enter a valid email')
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v)),
  // Required as of 2026-07-31 — every withdrawal needs a transaction PIN, and
  // wallet registration was the only signup path with no later screen that
  // ever prompted for one, silently leaving every wallet-registered member
  // unable to withdraw. Collected here, confirmed client-side, hashed
  // server-side the moment it's received (never stored/logged in the clear).
  transaction_pin: z
    .string()
    .regex(/^\d{4,8}$/, 'PIN must be 4-8 digits'),
  transaction_pin_confirmation: z
    .string()
    .regex(/^\d{4,8}$/, 'PIN must be 4-8 digits'),
})
  .refine((v) => v.transaction_pin === v.transaction_pin_confirmation, {
    message: 'PINs do not match',
    path: ['transaction_pin_confirmation'],
  });
export type WalletRegisterInput = z.infer<typeof walletRegisterInput>;

const walletRegisterResponse = z.object({
  status: z.enum(['pending', 'confirmed', 'rejected']),
  request: dappRegistrationRequestSchema,
  user: z
    .object({
      id: z.string(),
      user_code: z.string(),
    })
    .passthrough()
    .nullable(),
});
export type WalletRegisterResponse = z.infer<typeof walletRegisterResponse>;

export const walletLoginInput = z.object({
  wallet_address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
  nonce: z.string(),
  signature: z.string().regex(/^0x[a-fA-F0-9]{130}$/),
  code: z.string().optional(),
});
export type WalletLoginInput = z.infer<typeof walletLoginInput>;

const walletLoginResponse = z.object({
  requires_two_factor: z.boolean().default(false),
  session: sessionSchema.nullable().default(null),
});

const walletRegistrationInfoResponse = z.object({
  deposit_address: z.string(),
  chain: z.string(),
  chain_id: z.number(),
  token_address: z.string(),
  required_lds: z.string(),
});
export type WalletRegistrationInfo = z.infer<typeof walletRegistrationInfoResponse>;

export const walletAuthApi = {
  /**
   * PUBLIC — where + how much LDS a brand-new registrant must send before
   * they have an account (the authenticated /deposits/payment-details
   * endpoint is unreachable pre-registration).
   */
  registrationInfo: () => get('/auth/wallet/registration-info', resource(walletRegistrationInfoResponse)),

  /** Step 1 of both register and login: get a single-use nonce to sign. */
  nonce: async (input: WalletNonceInput) => {
    await getApiClient().ensureCsrfCookie();
    return post('/auth/wallet/nonce', resource(walletNonceResponse), input);
  },

  register: async (input: WalletRegisterInput) => {
    await getApiClient().ensureCsrfCookie();
    return post('/auth/wallet/register', resource(walletRegisterResponse), input);
  },

  login: async (input: WalletLoginInput) => {
    await getApiClient().ensureCsrfCookie();
    return post('/auth/wallet/login', walletLoginResponse, input);
  },

  /** Public — a not-yet-confirmed wallet has no session to check status with. */
  registrationStatus: (walletAddress: string) =>
    get('/auth/wallet/registration-status', resource(dappRegistrationRequestSchema.nullable()), {
      wallet_address: walletAddress,
    }),

  /** Authenticated — the logged-in member's own request ("My Wallet" screen). */
  myRequest: () => get('/wallet/dapp-request', resource(dappRegistrationRequestSchema.nullable())),
};
